import { PhoneNumberUtil } from 'google-libphonenumber';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ErrorResponse } from '../../../api/responses/error-response.ts';
import useFuelingService from '../../../hooks/fueling-services/useFuelingService.ts';
import useUpdateFuelingServiceMetadata from '../../../hooks/fueling-services/useUpdateFuelingServiceMetadata.ts';
import useUpdateFuelingServiceOperationHours from '../../../hooks/fueling-services/useUpdateFuelingServiceOperationHours.ts';
import useEnableFuelingService from '../../../hooks/fueling-services/useEnableFuelingService.ts';
import usePauseFuelingService from '../../../hooks/fueling-services/usePauseFuelingService.ts';
import useDisableFuelingService from '../../../hooks/fueling-services/useDisableFuelingService.ts';
import MutateUpdateFuelingServiceForm, {
  MutateUpdateFuelingServiceFormValues,
} from '../../ui/places/MutateUpdateFuelingServiceForm.tsx';
import {
  UpdateFuelingServiceMetadataRequest,
  UpdateFuelingServiceOperationHoursRequest,
  UpdateFuelingServicePaymentMethodsRequest,
  UpdateFuelingServiceTagsRequest,
} from '../../../api/requests/fueling-service-request.ts';
import { notifyError } from '../../../utils/NotifyError.tsx';
import { notifySuccess } from '../../../utils/NotifySuccess.tsx';
import { LoadingOverlay, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import useUpdateFuelingServiceTags from '../../../hooks/fueling-services/useUpdateFuelingServiceTags.ts';
import useUpdateFuelingServicePaymentMethods from '../../../hooks/fueling-services/useUpdateFuelingServicePaymentMethods.ts';
import useUpdateFuelingServiceStoreIds from '../../../hooks/fueling-services/useUpdateFuelingServiceStoreIds.ts';
import {
  buildStoreUrl,
  resolveStoreIdsChange,
} from '../../../utils/storeUrl.ts';

const phoneUtil = PhoneNumberUtil.getInstance();

interface UpdateFuelingServiceMetadataModalProps {
  placeId: string;
  fuelingServiceId: string | null;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const UpdateFuelingServiceMetadataDrawer = ({
  placeId,
  fuelingServiceId,
  onSuccess,
  onCancel,
}: UpdateFuelingServiceMetadataModalProps) => {
  const queryClient = useQueryClient();

  const { data: fuelingService, isLoading: isFuelingServiceLoading } =
    useFuelingService(fuelingServiceId);

  const { mutateAsync: updateFuelingServiceMetadata } =
    useUpdateFuelingServiceMetadata();
  const { mutateAsync: updateFuelingServiceTags } =
    useUpdateFuelingServiceTags();
  const { mutateAsync: updateFuelingServicePaymentMethods } =
    useUpdateFuelingServicePaymentMethods();
  const { mutateAsync: updateFuelingServiceOperationHours } =
    useUpdateFuelingServiceOperationHours();
  const { mutateAsync: updateFuelingServiceStoreIds } =
    useUpdateFuelingServiceStoreIds();
  const { mutateAsync: enableFuelingService } = useEnableFuelingService();
  const { mutateAsync: pauseFuelingService } = usePauseFuelingService();
  const { mutateAsync: disableFuelingService } = useDisableFuelingService();

  const { mutate: handleSubmitAll, isPending: isUpdateFuelingServicePending } =
    useMutation<void, ErrorResponse, MutateUpdateFuelingServiceFormValues>({
      mutationFn: async (formValues) => {
        const storeIdsToSubmit = resolveStoreIdsChange(
          formValues.storeIdUrl,
          'FUELING',
          fuelingService.storeIds,
        );
        if (storeIdsToSubmit !== null) {
          await updateFuelingServiceStoreIds({
            serviceId: fuelingService.id,
            storeIds: storeIdsToSubmit,
          });
        }

        const parsedPhoneNumber =
          formValues.phoneNumberCountryCode !== '' &&
          formValues.phoneNumberValue !== ''
            ? phoneUtil.parse(
                `${formValues.phoneNumberCountryCode}${formValues.phoneNumberValue}`,
              )
            : null;

        const updateFuelingServiceMetadataRequest: UpdateFuelingServiceMetadataRequest =
          {
            serviceId: formValues.serviceId,
            name: formValues.name,
            pricingSource: fuelingService.pricingSource,
            phoneNumber: {
              countryCode: formValues.phoneNumberCountryCode ?? '',
              number: parsedPhoneNumber
                ? phoneUtil.getNationalSignificantNumber(parsedPhoneNumber)
                : '',
            },
            website: formValues.website,
            notes: formValues.notes,
            brandId: formValues.brandId ?? '',
            supportedFuelTypes: fuelingService.supportedFuelTypes,
            supplier: fuelingService.supplier,
            ownershipType: fuelingService.ownershipType,
            hasRestroom: fuelingService.hasRestroom,
            hasAccessibleRestroom: fuelingService.hasAccessibleRestroom,
            selfServiceTypes: fuelingService.selfServiceTypes,
            parkingLotInfo: fuelingService.parkingLotInfo,
            convenienceStoreInfo: fuelingService.convenienceStoreInfo,
            carWashInfo: fuelingService.carWashInfo,
            carMaintenanceInfo: fuelingService.carMaintenanceInfo,
            airPumpInfo: fuelingService.airPumpInfo,
            waterPumpInfo: fuelingService.waterPumpInfo,
          };

        await updateFuelingServiceMetadata(updateFuelingServiceMetadataRequest);

        const updateFuelingServiceTagsRequest: UpdateFuelingServiceTagsRequest =
          {
            serviceId: formValues.serviceId,
            tags: formValues.tags,
          };

        await updateFuelingServiceTags(updateFuelingServiceTagsRequest);

        const updateFuelingServicePaymentMethodsRequest: UpdateFuelingServicePaymentMethodsRequest =
          {
            serviceId: formValues.serviceId,
            paymentMethods: formValues.paymentMethods,
          };

        await updateFuelingServicePaymentMethods(
          updateFuelingServicePaymentMethodsRequest,
        );

        if (formValues.serviceStatus === 'ONLINE') {
          await enableFuelingService(fuelingService.id);
        } else if (formValues.serviceStatus === 'TEMPORARY_OFFLINE') {
          await pauseFuelingService(fuelingService.id);
        } else {
          await disableFuelingService(fuelingService.id);
        }

        // 一律送出表單當下顯示的營業時間，確保 drawer 上看到的資料跟實際寫入的完全一致
        const updateFuelingServiceOperationHoursRequest: UpdateFuelingServiceOperationHoursRequest =
          {
            serviceId: fuelingService.id,
            operationHours: formValues.operationHours,
          };

        await updateFuelingServiceOperationHours(
          updateFuelingServiceOperationHoursRequest,
        );
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: ['fuelingServices', fuelingService.id],
          exact: true,
        });
        await queryClient.invalidateQueries({
          queryKey: ['places', placeId],
          exact: true,
        });
        notifySuccess('更新加油服務成功');
        onSuccess();
      },
      onError: (error) => {
        notifyError('更新加油服務失敗', error.message);
      },
    });

  const handleFormSubmit = (
    formValues: MutateUpdateFuelingServiceFormValues,
  ) => {
    let storeIdsToSubmit: ReturnType<typeof resolveStoreIdsChange>;
    try {
      storeIdsToSubmit = resolveStoreIdsChange(
        formValues.storeIdUrl,
        'FUELING',
        fuelingService.storeIds,
      );
    } catch {
      return;
    }

    if (storeIdsToSubmit === null) {
      handleSubmitAll(formValues);
      return;
    }

    const isUnbinding = storeIdsToSubmit.length === 0;
    modals.openConfirmModal({
      title: (
        <Text style={{ fontSize: 16, fontWeight: 700 }}>
          {isUnbinding ? '解除綁定商店' : '綁定商店'}
        </Text>
      ),
      children: (
        <Text size="sm">
          {isUnbinding
            ? '解除綁定後，目前的營業時間不會被清空，會維持最後一次同步的資料，確定要繼續嗎？'
            : '綁定後將會用 autoplus 現有的營業時間覆蓋這個服務目前的資料，確定要繼續嗎？'}
        </Text>
      ),
      labels: { confirm: '是，我確定', cancel: '取消' },
      confirmProps: isUnbinding ? { color: 'red' } : undefined,
      onConfirm: () => handleSubmitAll(formValues),
    });
  };

  const handleClose = () => {
    onCancel();
  };

  if (isFuelingServiceLoading) return <LoadingOverlay />;

  return (
    <MutateUpdateFuelingServiceForm
      initialValues={{
        serviceId: fuelingService.id,
        name: fuelingService.name,
        serviceStatus: fuelingService.serviceStatus,
        brandId: fuelingService.brandId,
        storeIdUrl: fuelingService.storeIds[0]
          ? buildStoreUrl(fuelingService.storeIds[0])
          : '',
        operationHours: fuelingService.operationHours,
        phoneNumberCountryCode: fuelingService.phoneNumber?.countryCode,
        phoneNumberValue: fuelingService.phoneNumber?.number,
        website: fuelingService.website,
        notes: fuelingService.notes,
        tags: fuelingService.tags,
        paymentMethods: fuelingService.paymentMethods,
      }}
      onSubmit={handleFormSubmit}
      onCancel={handleClose}
      isSubmitting={isUpdateFuelingServicePending}
      isUpdating={true}
    />
  );
};

export { UpdateFuelingServiceMetadataDrawer };
